import React, { useContext, useEffect, useState } from "react";
import BasicModal from "../../components/Modals/BasicModal";
import { fetchCouponPopupTotalDataPVIN } from "./PvinKip";
import { FilterContext } from "../../context/Filter";
import TotalTataCareCuponTable from "./TotalTataCareCuponTable";
import UsedTataCareCupon from "./UsedTataCareCupon";
import BalancedTataCareCuponTable from "./BalancedTataCareCuponTable";
import ExpiredTataCareCuponTable from "./ExpiredTataCareCuponTable";

const TataCareCuponCard = ({ data = [] }) => {
  const { arn: parArn, childArn } = useContext(FilterContext);

  const [totalCuponModal, setTotalCuponModal] = useState(false);
  const [usedCuponModal, setUsedCuponModal] = useState(false);
  const [balancedCuponModal, setBalancedCuponModal] = useState(false);
  const [expiredCuponModal, setExpiredCuponModal] = useState(false);

  const [totalCuponData, setTotalCuponData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchTotalCuponData = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetchCouponPopupTotalDataPVIN(parArn, childArn);

      if (!response || Object.keys(response || {}).length === 0) {
        throw new Error("No data found");
      }

      setTotalCuponData(response);
    } catch (err) {
      console.error("❌ Error fetching Tata Care Coupon data:", err);
      setError("Failed to fetch Tata Care Coupon data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (parArn || childArn) {
      fetchTotalCuponData();
    }
  }, [parArn, childArn]);

  // ✅ Spinner Loader Component
  const Loader = () => (
    <div className="flex flex-col items-center justify-center gap-2">
      <div className="w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  );

  if (loading)
    return (
      <div className="h-[228px] w-[331px] border rounded-md flex items-center justify-center bg-white shadow-sm">
        <Loader />
      </div>
    );

  if (error)
    return (
      <div className="h-[228px] w-[331px] border rounded-md flex items-center justify-center bg-white shadow-sm">
        <p className="text-red-500">{error}</p>
      </div>
    );

  // ✅ Safe data extraction
  const delightData = data?.Delight_and_Earned_Points || [];

  const Earned_Total_Points = delightData?.[0]?.Earned_Total_Points ?? "—";
  const Earned_Used_Points = delightData?.[0]?.Earned_Used_Points ?? "—";
  const Earned_Balance_Points = delightData?.[0]?.Earned_Balance_Points ?? "—";
  const Earned_Expired_Points = delightData?.[0]?.Earned_Expired_Points ?? "—";

  const Delight_Total_Points =
    delightData?.[1]?.Delight_Total_Points?.toFixed?.(2) ?? "—";
  const Delight_Used_Points =
    delightData?.[1]?.Delight_Used_Points?.toFixed?.(2) ?? "—";
  const Delight_Balance_Points =
    delightData?.[1]?.Delight_Balance_Points?.toFixed?.(2) ?? "—";

  // ✅ Prevent premature render if data still "NA"
  const isDataLoaded =
    delightData?.length > 0 &&
    Earned_Total_Points !== "NA" &&
    Earned_Total_Points !== undefined;

  if (!isDataLoaded) {
    return (
      <div className="h-[228px] w-[331px] border rounded-md flex items-center justify-center bg-white shadow-sm">
        <Loader />
      </div>
    );
  }

  return (
    <div className="h-[228px] w-[331px] border rounded-md p-2 flex flex-col gap-4 bg-white shadow-sm">
      {/* === Tata Care Coupon Section === */}
      <div>
        <p className="font-semibold">TATA Care Coupon</p>
        <div className="flex flex-col gap-2">
          <div className="bg-[#1467b3] flex justify-between items-center text-white p-1 w-full">
            <p className="text-center">Total</p>
            <p className="text-center">Used</p>
            <p className="text-center">Balance</p>
            <p className="text-center">Expired</p>
          </div>

          <div className="flex justify-between items-center p-1 w-full">
            {/* Total */}
            <BasicModal
              open={totalCuponModal}
              handleOpenModal={() => setTotalCuponModal(true)}
              handleCloseModal={() => setTotalCuponModal(false)}
              title={"Total Tata Care Coupon"}
              trigger={
                <p
                  className="text-center text-blue-600 cursor-pointer"
                  onClick={() => setTotalCuponModal(true)}
                >
                  {Earned_Total_Points}
                </p>
              }
            >
              <TotalTataCareCuponTable data={totalCuponData} />
            </BasicModal>

            {/* Used */}
            <BasicModal
              open={usedCuponModal}
              handleOpenModal={() => setUsedCuponModal(true)}
              handleCloseModal={() => setUsedCuponModal(false)}
              title={"Used Tata Care Coupon"}
              trigger={
                <p
                  className="text-center text-blue-600 cursor-pointer"
                  onClick={() => setUsedCuponModal(true)}
                >
                  {Earned_Used_Points}
                </p>
              }
            >
              <UsedTataCareCupon />
            </BasicModal>

            {/* Balance */}
            <BasicModal
              open={balancedCuponModal}
              handleOpenModal={() => setBalancedCuponModal(true)}
              handleCloseModal={() => setBalancedCuponModal(false)}
              title={"Balanced Tata Care Coupon"}
              trigger={
                <p
                  className="text-center text-blue-600 cursor-pointer"
                  onClick={() => setBalancedCuponModal(true)}
                >
                  {Earned_Balance_Points}
                </p>
              }
            >
              <BalancedTataCareCuponTable />
            </BasicModal>

            {/* Expired */}
            <BasicModal
              open={expiredCuponModal}
              handleOpenModal={() => setExpiredCuponModal(true)}
              handleCloseModal={() => setExpiredCuponModal(false)}
              title={"Expired Tata Care Coupon"}
              trigger={
                <p
                  className="text-center text-blue-600 cursor-pointer"
                  onClick={() => setExpiredCuponModal(true)}
                >
                  {Earned_Expired_Points}
                </p>
              }
            >
              <ExpiredTataCareCuponTable />
            </BasicModal>
          </div>
        </div>
      </div>

      {/* === Loyalty Points Section === */}
      <div>
        <p className="font-semibold">Loyalty Points</p>
        <div className="flex flex-col gap-2">
          <div className="bg-[#1467b3] flex justify-between items-center text-white p-1 w-full">
            <p className="text-center">Total</p>
            <p className="text-center">Used</p>
            <p className="text-center">Balance</p>
          </div>
          <div className="flex justify-between items-center p-1 w-full">
            <p className="text-center">{Delight_Total_Points}</p>
            <p className="text-center">{Delight_Used_Points}</p>
            <p className="text-center">{Delight_Balance_Points}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TataCareCuponCard;
